import type { ResumeData, ResumeSettings } from '../types/resume';
import { mergeSettings, formatDate, pagePadding } from '../lib/resume-settings';

interface Props {
  data: ResumeData;
  settings?: ResumeSettings;
}

function renderDescription(desc: string, lineSpacing: number, bScale: number) {
  if (!desc) return null;
  const lines = desc.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const style = { fontSize: `${0.75 * bScale}rem`, lineHeight: lineSpacing };
  if (lines.length <= 1) {
    return <p className="text-slate-600 whitespace-pre-line" style={style}>{desc}</p>;
  }
  return (
    <ul className="list-disc pl-4 space-y-1 text-slate-600" style={style}>
      {lines.map((line, i) => <li key={i}>{line.replace(/^[-•\s]+/, '')}</li>)}
    </ul>
  );
}

export function CompactTemplate({ data, settings }: Props) {
  const { personal, experience, education, skills, projects, certifications } = data;
  const s = mergeSettings(settings);
  const hScale = s.sizes.heading / 100;
  const bScale = s.sizes.body / 100;

  return (
    <div
      className="bg-white text-slate-800 h-full"
      style={{
        fontFamily: `${s.fonts.primary}, system-ui, sans-serif`,
        padding: pagePadding(s.margins),
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="py-4 border-b-2 border-slate-800 mb-4" style={{ textAlign: s.headerAlignment }}>
        {s.photo && <img src={s.photo} alt="" className="w-14 h-14 rounded-full object-cover mx-auto mb-2 border border-slate-800" />}
        <h1 className="font-bold text-slate-900 uppercase tracking-wide" style={{ fontFamily: `${s.fonts.secondary}, system-ui, sans-serif`, fontSize: `${1.5 * hScale}rem` }}>
          {personal.fullName || 'Your Name'}
        </h1>
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-slate-600 justify-center" style={{ fontSize: `${0.75 * bScale}rem` }}>
          {personal.email && <span>{personal.email}</span>}
          {personal.phone && <span>• {personal.phone}</span>}
          {personal.location && <span>• {personal.location}</span>}
          {personal.linkedin && <span>• {personal.linkedin}</span>}
          {personal.website && <span>• {personal.website}</span>}
        </div>
      </header>

      <main style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.sectionGap }}>
        {personal.summary && (
          <section>
            <p className="text-slate-700 whitespace-pre-line" style={{ fontSize: `${0.8 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{personal.summary}</p>
          </section>
        )}

        {experience.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-wider text-slate-900 border-b border-slate-300 pb-1 mb-2" style={{ fontSize: `${0.75 * hScale}rem` }}>WORK EXPERIENCE</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {experience.map((exp) => (
                <div key={exp.id}>
                  <div className="flex justify-between items-baseline">
                    <h3 className="font-bold text-slate-900" style={{ fontSize: `${0.875 * hScale}rem` }}>{exp.title}</h3>
                    <span className="text-slate-500" style={{ fontSize: `${0.75 * bScale}rem` }}>
                      {formatDate(exp.startDate, s.dateFormat)}—{exp.current ? 'Present' : formatDate(exp.endDate, s.dateFormat)}
                    </span>
                  </div>
                  <p className="text-slate-700 font-medium" style={{ fontSize: `${0.75 * bScale}rem` }}>{exp.company}{exp.location && `, ${exp.location}`}</p>
                  {renderDescription(exp.description, s.spacing.lineSpacing, bScale)}
                </div>
              ))}
            </div>
          </section>
        )}

        {skills.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-wider text-slate-900 border-b border-slate-300 pb-1 mb-2" style={{ fontSize: `${0.75 * hScale}rem` }}>Skills</h2>
            <div className="flex flex-wrap gap-2">
              {skills.map((skill) => (
                <span key={skill.id} className="inline-flex items-center rounded-md bg-slate-700 px-3 py-1.5 text-xs font-medium text-white" style={{ fontSize: `${0.625 * bScale}rem` }}>
                  {skill.name}
                </span>
              ))}
            </div>
          </section>
        )}

        {education.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-wider text-slate-900 border-b border-slate-300 pb-1 mb-2" style={{ fontSize: `${0.75 * hScale}rem` }}>Education</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {education.map((edu) => (
                <div key={edu.id} className="flex justify-between items-baseline">
                  <div>
                    <span className="font-bold text-slate-900" style={{ fontSize: `${0.875 * hScale}rem` }}>{edu.school}</span>
                    <span className="text-slate-600" style={{ fontSize: `${0.75 * bScale}rem` }}> — {edu.degree}{edu.field && `, ${edu.field}`}</span>
                  </div>
                  <span className="text-slate-500" style={{ fontSize: `${0.75 * bScale}rem` }}>
                    {formatDate(edu.startDate, s.dateFormat)}—{formatDate(edu.endDate, s.dateFormat)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {projects.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-wider text-slate-900 border-b border-slate-300 pb-1 mb-2" style={{ fontSize: `${0.75 * hScale}rem` }}>Projects</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {projects.map((proj) => (
                <div key={proj.id}>
                  <div className="flex justify-between items-baseline">
                    <span className="font-bold text-slate-900" style={{ fontSize: `${0.875 * hScale}rem` }}>{proj.name}</span>
                    {proj.url && <a href={proj.url} className="text-blue-600" style={{ fontSize: `${0.75 * bScale}rem` }}>Link</a>}
                  </div>
                  <p className="text-slate-600 mt-0.5" style={{ fontSize: `${0.75 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{proj.description}</p>
                  {proj.technologies.length > 0 && <p className="text-slate-500 mt-0.5" style={{ fontSize: `${0.75 * bScale}rem` }}>Tech: {proj.technologies.join(', ')}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {certifications.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-wider text-slate-900 border-b border-slate-300 pb-1 mb-2" style={{ fontSize: `${0.75 * hScale}rem` }}>Certifications</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {certifications.map((cert) => (
                <div key={cert.id} className="flex justify-between items-baseline">
                  <span className="text-slate-800" style={{ fontSize: `${0.75 * bScale}rem` }}>{cert.name}</span>
                  <span className="text-slate-500" style={{ fontSize: `${0.75 * bScale}rem` }}>{cert.issuer}, {cert.date}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
